import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";
import { randomId } from "@/shared/utils/randomId";

// Status enum matching backend
export const RequisitionStatus = {
  Draft: 0,
  Pending: 1,
  PendingApproval: 1,
  Approved: 2,
  PartiallyIssued: 3,
  Issued: 4,
  Rejected: 5,
  Cancelled: 6,
} as const;

export type RequisitionStatus = typeof RequisitionStatus[keyof typeof RequisitionStatus];

const STATUS_LABELS: Record<number, string> = {
  0: "Draft",
  1: "Pending Approval",
  2: "Approved",
  3: "Partially Issued",
  4: "Issued",
  5: "Rejected",
  6: "Cancelled",
};

const STATUS_NAME_TO_VALUE: Record<string, number> = {
  draft: RequisitionStatus.Draft,
  pending: RequisitionStatus.PendingApproval,
  pendingapproval: RequisitionStatus.PendingApproval,
  approved: RequisitionStatus.Approved,
  partiallyissued: RequisitionStatus.PartiallyIssued,
  issued: RequisitionStatus.Issued,
  rejected: RequisitionStatus.Rejected,
  cancelled: RequisitionStatus.Cancelled,
};

export const getStatusLabel = (status: number | string): string => {
  const statusNum = typeof status === "string"
    ? (STATUS_NAME_TO_VALUE[status.replace(/\s+/g, "").toLowerCase()] ?? parseInt(status))
    : status;
  return STATUS_LABELS[statusNum] || "Unknown";
};

export interface RequisitionItem {
  id?: string;
  // Null on customer-seed lines — those reference seedBatchId instead. Backend enforces XOR.
  inputId: string | null;
  inputName?: string;
  inputCode?: string;
  seedBatchId?: string | null;
  seedBatchNumber?: string;
  seedOwnershipType?: number | string | null;
  seedOwnerName?: string | null;
  seedBatchCustomerId?: string | null;
  seedBatchCustomerName?: string | null;
  orderNumber?: string | null;
  quantityRequested: number;
  quantityApproved?: number;
  quantityIssued?: number;
  quantityReturned?: number;
  quantityConsumed?: number;
  usesSeedReservation?: boolean;
  unitOfMeasure: string;
  orderId?: string;
  orderItemId?: string;
  sowingInstructionId?: string;
  sowingInstructionNumber?: string;
  isExternalItem?: boolean;
  returnStockMovementId?: string;
  firstReturnedAt?: string;
  lastReturnedAt?: string;
  returnNotes?: string;
}

export interface CreateRequisitionRequest {
  requiredByDate: string;
  purpose: string;
  departmentId: string;
  greenhouseId?: string;
  farmId?: string;
  fieldId?: string;
  notes: string;
  items: RequisitionItem[];
}

export interface UpdateRequisitionRequest {
  id: string;
  requiredByDate: string;
  purpose: string;
  departmentId: string;
  greenhouseId?: string;
  farmId?: string;
  fieldId?: string;
  notes: string;
  items: RequisitionItem[];
}

export interface ApproveRequisitionItem {
  itemId: string;
  quantityApproved: number;
  unitOfMeasure: number;
}

export interface ApproveRequisitionRequest {
  requisitionId: string;
  approvalNotes?: string;
  items: ApproveRequisitionItem[];
}

export interface RejectRequisitionRequest {
  id: string;
  reason: string;
}

export interface Requisition {
  id: string;
  requisitionNumber?: string;
  requiredByDate: string;
  purpose: string;
  departmentId: string;
  departmentName: string;
  greenhouseId?: string;
  greenhouseName?: string;
  farmId?: string;
  farmName?: string;
  fieldId?: string;
  fieldName?: string;
  notes: string;
  items: RequisitionItem[];
  status: number;
  requisitionStatus?: number;
  requestedByUserName?: string;
  requiresProcurement?: boolean;
  createdAt: string;
  createdBy: string;
}

export interface ReturnRequisitionItemRequest {
  idempotencyKey?: string;
  requisitionId: string;
  itemId: string;
  quantityReturned: number;
  unitOfMeasure: number; // UnitOfMeasure enum value
  notes?: string;
  quantityUsed?: number;
  sowingInstructionId?: string;
  /** ISO datetime when the items were physically returned. Defaults to today. */
  returnedDate?: string;
}

export const RequisitionReturnApprovalStatus = {
  Draft: 0,
  PendingApproval: 1,
  Approved: 2,
  Rejected: 3,
} as const;

export type RequisitionReturnApprovalStatus =
  (typeof RequisitionReturnApprovalStatus)[keyof typeof RequisitionReturnApprovalStatus];

export interface RequisitionItemReturn {
  id: string;
  requisitionItemId: string;
  requisitionId: string;
  requisitionNumber: string;
  inputName: string;
  unitOfMeasure: string;
  quantity: number;
  quantityUsed: number;
  quantityIssuedOnItem: number;
  quantityAlreadyApprovedReturned: number;
  isCustomerSeedReturn: boolean;
  seedBatchId?: string;
  seedBatchNumber?: string;
  sowingInstructionId?: string;
  sowingInstructionNumber?: string;
  submittedByUserId: string;
  submittedByUserName: string;
  submittedAt: string;
  returnedDate?: string;
  submissionNotes?: string;
  status: number;
  approvedByUserId?: string;
  approvedByUserName?: string;
  approvedAt?: string;
  approvalNotes?: string;
  rejectionReason?: string;
  stockMovementId?: string;
}

export interface ApproveRequisitionItemReturnRequest {
  returnId: string;
  approvalNotes?: string;
}

export interface RejectRequisitionItemReturnRequest {
  returnId: string;
  rejectionReason: string;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
  totalRecords?: number;
  currentPage: number;
  pageNumber?: number;
  pageSize: number;
  totalPages: number;
}

export type RequisitionView = "all" | "mine" | "pendingApproval" | "pendingIssue";

export interface RequisitionListParams {
  pageNumber?: number;
  pageSize?: number;
  searchTerm?: string;
  searchField?: string;
  sortBy?: string;
  sortDescending?: boolean;
  startDate?: string;
  endDate?: string;
  greenhouseId?: string;
  view?: RequisitionView;
}

const normalizePagedResult = <T>(result: PagedResult<T>): PagedResult<T> => ({
  ...result,
  totalCount: result.totalCount ?? result.totalRecords ?? 0,
  currentPage: result.currentPage ?? result.pageNumber ?? 1,
});

export const requisitionApi = {
  getAllRequisitions: async (pageNumber = 1, pageSize = 20, search?: string): Promise<PagedResult<Requisition>> => {
    return requisitionApi.getRequisitionsPaged({
      pageNumber,
      pageSize,
      searchTerm: search,
      view: "all",
    });
  },

  getRequisitionsPaged: async (params: RequisitionListParams): Promise<PagedResult<Requisition>> => {
    const { data } = await apiClient.get<ApiResponse<PagedResult<Requisition>>>("/Requisitions", {
      params,
    });
    return normalizePagedResult(data.data);
  },

  getMyRequisitions: async (): Promise<Requisition[]> => {
    const { data } = await apiClient.get<ApiResponse<Requisition[]>>(
      "/Requisitions/my-requisitions"
    );
    return data.data;
  },

  getPendingApprovals: async (): Promise<Requisition[]> => {
    const { data } = await apiClient.get<ApiResponse<Requisition[]>>(
      "/Requisitions/pending-approvals"
    );
    return data.data;
  },

  getPendingIssue: async (): Promise<Requisition[]> => {
    const { data } = await apiClient.get<ApiResponse<Requisition[]>>(
      "/Requisitions/pending-issue"
    );
    return data.data;
  },

  getRequisitionsByDateRange: async (
    startDate: string,
    endDate: string
  ): Promise<Requisition[]> => {
    const { data } = await apiClient.get<ApiResponse<Requisition[]>>(
      "/Requisitions/by-date-range",
      {
        params: {
          startDate,
          endDate,
        },
      }
    );
    return data.data;
  },

  getRequisitionsByGreenhouse: async (
    greenhouseId: string
  ): Promise<Requisition[]> => {
    const { data } = await apiClient.get<ApiResponse<Requisition[]>>(
      `/Requisitions/by-greenhouse/${greenhouseId}`
    );
    return data.data;
  },

  getRequisitionById: async (id: string): Promise<Requisition> => {
    const { data } = await apiClient.get<ApiResponse<Requisition>>(
      `/Requisitions/${id}`
    );
    return data.data;
  },

  createRequisition: async (
    payload: CreateRequisitionRequest
  ): Promise<Requisition> => {
    const { data } = await apiClient.post<ApiResponse<Requisition>>(
      "/Requisitions",
      payload
    );
    return data.data;
  },

  updateRequisition: async (
    payload: UpdateRequisitionRequest
  ): Promise<Requisition> => {
    const { data } = await apiClient.put<ApiResponse<Requisition>>(
      `/Requisitions/${payload.id}`,
      payload
    );
    return data.data;
  },

  deleteRequisition: async (id: string): Promise<void> => {
    await apiClient.delete(`/Requisitions/${id}`);
  },

  approveRequisition: async (
    payload: ApproveRequisitionRequest
  ): Promise<Requisition> => {
    const { data } = await apiClient.post<ApiResponse<Requisition>>(
      `/Requisitions/approve`,
      {
        requisitionId: payload.requisitionId,
        approvalNotes: payload.approvalNotes,
        items: payload.items,
      }
    );
    return data.data;
  },

  rejectRequisition: async (
    payload: RejectRequisitionRequest
  ): Promise<Requisition> => {
    const { data } = await apiClient.post<ApiResponse<Requisition>>(
      `/Requisitions/${payload.id}/reject`,
      { reason: payload.reason }
    );
    return data.data;
  },

  returnItem: async (
    { idempotencyKey, ...payload }: ReturnRequisitionItemRequest
  ): Promise<RequisitionItemReturn> => {
    const { data } = await apiClient.post<ApiResponse<RequisitionItemReturn>>(
      `/Requisitions/${payload.requisitionId}/items/${payload.itemId}/return`,
      payload,
      { headers: { "Idempotency-Key": idempotencyKey ?? randomId() } }
    );
    return data.data;
  },

  getPendingReturns: async (): Promise<RequisitionItemReturn[]> => {
    const { data } = await apiClient.get<ApiResponse<RequisitionItemReturn[]>>(
      "/Requisitions/returns/pending"
    );
    return data.data ?? [];
  },

  getMyReturns: async (): Promise<RequisitionItemReturn[]> => {
    const { data } = await apiClient.get<ApiResponse<RequisitionItemReturn[]>>(
      "/Requisitions/returns/mine"
    );
    return data.data ?? [];
  },

  approveReturn: async (
    payload: ApproveRequisitionItemReturnRequest
  ): Promise<RequisitionItemReturn> => {
    const { data } = await apiClient.post<ApiResponse<RequisitionItemReturn>>(
      `/Requisitions/returns/${payload.returnId}/approve`,
      payload
    );
    return data.data;
  },

  rejectReturn: async (
    payload: RejectRequisitionItemReturnRequest
  ): Promise<RequisitionItemReturn> => {
    const { data } = await apiClient.post<ApiResponse<RequisitionItemReturn>>(
      `/Requisitions/returns/${payload.returnId}/reject`,
      payload
    );
    return data.data;
  },

  getRequisitionForSowing: async (
    sowingInstructionId: string
  ): Promise<Requisition | null> => {
    const { data } = await apiClient.get<ApiResponse<Requisition | null>>(
      `/SowingInstructions/${sowingInstructionId}/requisition`
    );
    return data.data ?? null;
  },

  getRequisitionsForSowing: async (
    sowingInstructionId: string
  ): Promise<Requisition[]> => {
    const { data } = await apiClient.get<ApiResponse<Requisition[]>>(
      `/SowingInstructions/${sowingInstructionId}/requisitions`
    );
    return data.data ?? [];
  },
};
